import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useOrgDocuments, useOrgJobs, useUploadDocument, useDeleteDocument, useBulkGenerate } from "@/hooks/useOrgKnowledge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Upload, Trash2, Sparkles, Loader2, CheckCircle2, XCircle, Clock, BookOpen } from "lucide-react";

const STATUS_STYLES = {
  completed: { icon: CheckCircle2, cls: "text-primary" },
  failed: { icon: XCircle, cls: "text-destructive" },
  running: { icon: Loader2, cls: "text-primary animate-spin" },
  pending: { icon: Clock, cls: "text-muted-foreground" },
};

export function KnowledgeBase() {
  const fileRef = useRef(null);
  const { data: documents = [] } = useOrgDocuments();
  const { data: jobs = [] } = useOrgJobs();
  const [selected, setSelected] = useState([]);
  const [mode, setMode] = useState("bites");
  const [topic, setTopic] = useState("");

  const upload = useUploadDocument({
    onSuccess: (d) => toast.success(`Uploaded ${d.filename}`),
    onError: (e) => toast.error(apiError(e)),
  });
  const del = useDeleteDocument({
    onSuccess: () => { toast.success("Document removed"); setSelected((s) => s); },
    onError: (e) => toast.error(apiError(e)),
  });
  const bulk = useBulkGenerate({
    onSuccess: () => { toast.success("Bulk generation started — courses will appear in the Team Library"); setSelected([]); setTopic(""); },
    onError: (e) => toast.error(apiError(e)),
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload.mutate(f);
    e.target.value = "";
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const generate = () => {
    if (selected.length === 0) { toast.error("Select at least one document"); return; }
    if (mode === "comprehensive" && !topic.trim()) { toast.error("Enter a course topic/title"); return; }
    bulk.mutate({ doc_ids: selected, mode, topic: topic.trim() || null });
  };

  return (
    <div className="mb-10" data-testid="knowledge-base">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="h-5 w-5 text-primary" />
        <h2 className="font-display text-2xl font-bold tracking-tight">Internal Knowledge Engine</h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Documents */}
        <div className="border border-border bg-card p-6" data-testid="documents-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Knowledge documents</h3></div>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={onFile} data-testid="knowledge-file-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={upload.isPending} className="rounded-none bg-primary hover:opacity-90 gap-1" data-testid="upload-document-button">
              {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">PDF, DOCX, TXT or MD — up to 10 MB. Courses your team generates are grounded in these files.</p>
          {documents.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-documents">No documents yet. Upload your first internal file.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="documents-list">
              {documents.map((d) => (
                <label key={d.id} className="flex items-center gap-3 border border-border p-3 text-sm cursor-pointer hover:bg-secondary/50" data-testid={`document-${d.id}`}>
                  <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} className="accent-primary h-4 w-4" data-testid={`select-doc-${d.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{d.filename}</div>
                    <div className="text-xs text-muted-foreground">{(d.char_count || 0).toLocaleString()} chars · {d.uploaded_by}</div>
                  </div>
                  <button onClick={(e) => { e.preventDefault(); del.mutate(d.id); }} className="text-muted-foreground hover:text-destructive" data-testid={`delete-doc-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Bulk generator */}
        <div className="border border-border bg-card p-6" data-testid="bulk-generator-card">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="h-5 w-5 text-primary" /><h3 className="font-display font-bold">Bulk course generator</h3></div>
          <p className="text-xs text-muted-foreground mb-4">{selected.length} document{selected.length === 1 ? "" : "s"} selected.</p>

          <Label className="text-xs uppercase tracking-wider mb-2 block">Output</Label>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button onClick={() => setMode("bites")} className={`border p-3 text-left text-sm ${mode === "bites" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="mode-bites">
              <div className="font-semibold">Bites</div>
              <div className="text-xs text-muted-foreground">One short course per document</div>
            </button>
            <button onClick={() => setMode("comprehensive")} className={`border p-3 text-left text-sm ${mode === "comprehensive" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="mode-comprehensive">
              <div className="font-semibold">Comprehensive</div>
              <div className="text-xs text-muted-foreground">One deep course from all docs</div>
            </button>
          </div>

          {mode === "comprehensive" && (
            <div className="mb-4">
              <Label className="text-xs uppercase tracking-wider mb-2 block">Course title / topic</Label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. 2026 Onboarding & Compliance" className="rounded-none h-11" data-testid="bulk-topic-input" />
            </div>
          )}

          <Button onClick={generate} disabled={bulk.isPending} className="rounded-none h-11 w-full bg-primary hover:opacity-90 gap-1" data-testid="bulk-generate-button">
            {bulk.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate courses
          </Button>

          {/* Jobs */}
          <div className="mt-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Generation jobs</p>
            {jobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No generation jobs yet.</p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="jobs-list">
                {jobs.map((j) => {
                  const st = STATUS_STYLES[j.status] || STATUS_STYLES.pending;
                  const Icon = st.icon;
                  const pct = j.total ? Math.round((j.progress / j.total) * 100) : 0;
                  return (
                    <motion.div key={j.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border border-border p-3 text-sm" data-testid={`job-${j.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium capitalize">{j.mode}{j.topic ? ` · ${j.topic}` : ""}</span>
                        <span className={`inline-flex items-center gap-1 text-xs ${st.cls}`}><Icon className="h-3.5 w-3.5" /> {j.status}</span>
                      </div>
                      <div className="mt-2 h-1.5 bg-secondary">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid={`job-progress-${j.id}`} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{j.progress}/{j.total} course(s){j.error ? ` · ${j.error}` : ""}</div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
